import { debounce } from 'lodash'
import { useCallback, useState } from 'react'
import { handleError } from '../../../lib/utils'
import { Form, message } from 'antd'
import {
  MedPraxSchemePlanOptionSearch,
  MedPraxSchemePlanSearch,
  MedPraxSchemeSearch
} from '../../../Scripting/utils/utils'
import { batch, useDispatch, useSelector } from 'react-redux'
import systemUsersReducer from '../utils/reducer'
import coreReducer from '../../../../core/utils/reducer'
import { createAgiliteUser, findAgiliteUser } from '../../../Auth/utils/utils'
import { form } from 'framer-motion/client'

// Validate ID number (South African ID validation)
const validateID = async ({ idType, idInput }) => {
  // For passports and foreign IDs, always return true
  if (idType !== 'sa') return true

  // For SA IDs, perform validation
  if (!idInput || idInput.length !== 13) return false

  // Check if all characters are digits
  if (!/^\d+$/.test(idInput)) return false

  // More complex validation could be added here
  return true
}

// Function to create a patient with proper handling of dependants
const addPatient = async ({ formData, phoneNumber, isDependant, mainPatientInfo }) => {
  // Ensure medical aid data is preserved
  if (formData.medicalAid) {
  }

  // Create the user
  const newUser = await createAgiliteUser({
    ...formData,
    phoneNumber,
    extraData: {
      role: {
        type: 'patient'
      },
      isActive: true
    },
    mainMemberId: isDependant && mainPatientInfo?._id ? mainPatientInfo._id : ''
  })

  return newUser
}

const useAddPatient = ({
  isBooking = false,
  isDependant = false,
  mainPatientInfo = null,
  refreshView = () => {},
  resetFunction = () => {},
  setAddingForBooking = () => {},
  setBookingPatient = () => {},
  type = 'patient',
  shouldStayOnPage = false,
  onPatientSaved = null,
  editRecord
}) => {
  const activeRecords = useSelector((state) => state.systemUsers.activeRecords)
  const authState = useSelector((state) => state.auth)
  const [loading, setLoading] = useState(false)
  const [stayOnPage, setStayOnPage] = useState(shouldStayOnPage)
  const [savedPatient, setSavedPatient] = useState(null)
  const dispatch = useDispatch()
  const [specialFormState, setSpecialFormState] = useState([])
  const [dependants, setDependants] = useState([
    { key: new Date().toISOString(), firstName: '', lastName: '', gender: '', dateOfBirth: '' }
  ])
  const [idType, setIdType] = useState('id')
  const [patientForm] = Form.useForm()

  const dependantTemplate = () => {
    return { key: new Date().toISOString(), firstName: '', lastName: '', gender: '', dateOfBirth: '' }
  }

  const residentVisitor = null
  // Medical aid capturing
  const [schemeLoading, setSchemeLoading] = useState(false)
  const [schemeData, setSchemeData] = useState([])
  const [plansLoading, setPlansLoading] = useState(false)
  const [plansData, setPlansData] = useState([])
  const [planOptionsLoading, setPlanOptionsLoading] = useState(false)
  const [planOptionData, setPlanOptionData] = useState([])

  // Dependant Handling
  const handleDepChange = (value, index, field) => {
    const tmpDependants = [...dependants]
    tmpDependants[index][field] = value
    setDependants(tmpDependants)
  }

  const handleAddDep = () => {
    setDependants([...dependants, dependantTemplate()])
  }

  const handleRemoveDep = (index) => {
    const tmpDependants = [...dependants]
    tmpDependants.splice(index, 1)
    setDependants(tmpDependants)
  }

  const handleSubmit = async (targetType) => {
    setLoading(true)

    try {
      await patientForm.validateFields()

      let shouldCloseTab = true

      if (stayOnPage) {
        shouldCloseTab = false
      }

      // Get the raw form values
      let valuesObj = patientForm.getFieldsValue()

      // Explicitly ensure medical aid codes are preserved
      if (valuesObj.medicalAid) {
        // Get the current medical aid data from the form
        const medicalAidData = patientForm.getFieldValue('medicalAid') || {}

        // Make sure all codes are explicitly set in the values object
        valuesObj.medicalAid = {
          ...medicalAidData,
          name: medicalAidData.name || '',
          schemeCode: medicalAidData.schemeCode || '',
          plan: medicalAidData.plan || '',
          planCode: medicalAidData.planCode || '',
          planOption: medicalAidData.planOption || '',
          planOptionCode: medicalAidData.planOptionCode || '',
          number: medicalAidData.number || '',
          dependantNumber: medicalAidData.dependantNumber || ''
        }
      }

      // If validation passes, submit the form
      const isValidId = await validateID({ idType, idInput: valuesObj.idNo })

      if (idType === 'sa' && !isValidId) {
        message.error('Invalid ID Number')
        setLoading(false)
        return
      }

      // New Patient
      let phoneNumber = patientForm.getFieldValue('phoneNumber')

      if (phoneNumber) {
        phoneNumber = formatNumber(phoneNumber)
      }

      // Only check for duplicate phone numbers if this is not a dependant
      // Or if the phone number is different from the main member's
      let skipPhoneCheck = false

      // For dependants, we'll always allow using the same phone number as the main member
      if (isDependant && mainPatientInfo) {
        const mainPhoneNumber = formatNumber(mainPatientInfo.phoneNumber || '')

        if (phoneNumber === mainPhoneNumber) {
          skipPhoneCheck = true
        }
      }

      // Comprehensive duplicate checking
      const email = valuesObj.email
      const idNumber = valuesObj.idNo

      console.log('🔍 Checking duplicates for:', { email, idNumber, phoneNumber })

      // Check for duplicate email
      if (email) {
        const existingUserByEmail = await findAgiliteUser({ email })
        if (existingUserByEmail) {
          console.log('❌ Duplicate email found:', existingUserByEmail._id)
          message.error('A user with this email address already exists')
          setLoading(false)
          return
        }
      }

      // Check for duplicate ID number
      if (idNumber) {
        const existingUserById = await findAgiliteUser({ idNo: idNumber })
        if (existingUserById) {
          console.log('❌ Duplicate ID number found:', existingUserById._id)
          message.error('A user with this ID/Passport number already exists')
          setLoading(false)
          return
        }
      }

      // Check for duplicate phone number only if we're not skipping the check
      if (!skipPhoneCheck && phoneNumber) {
        const existingUserByPhone = await findAgiliteUser({ phoneNumber })
        if (existingUserByPhone) {
          message.error('A user with this phone number already exists')
          setLoading(false)
          return
        }
      }

      const addPatientRes = await addPatient({
        formData: valuesObj,
        phoneNumber,
        isDependant,
        mainPatientInfo
      })

      // Store the saved patient
      setSavedPatient(addPatientRes)

      // Call the onPatientSaved callback if provided
      if (onPatientSaved && typeof onPatientSaved === 'function') {
        onPatientSaved(addPatientRes)
      }

      // Handle booking if needed
      if (isBooking) {
        setBookingPatient(addPatientRes)
        setAddingForBooking(false)
      } else {
        // Update the active records
        const tmpActiveRecords = [...activeRecords]
        tmpActiveRecords.push(addPatientRes)

        batch(() => {
          dispatch(systemUsersReducer.actions.setActiveRecords(tmpActiveRecords))
        })

        // Show success message
        message.success('Patient created successfully')

        // Only reset and navigate away if stayOnPage is false

        if (isDependant) {
          // Reset form for adding another dependant
          patientForm.resetFields()

          // If it's a dependant and we're staying on page, refresh the dependant list
          refreshView()
        } else {
          // Reset the form
          handleReset()
          // Refresh the view (this causes navigation back to list)
          refreshView()
          closeTab()
          editRecord(addPatientRes, true)
        }
      }
    } catch (error) {
      message.error(handleError(error, true))
    }

    setLoading(false)
  }

  const handleReset = () => {
    patientForm.resetFields()
    setDependants([dependantTemplate()])
    resetFunction()
  }

  const formatNumber = (phoneNumber) => {
    if (!phoneNumber) return ''

    // Remove any existing country code and formatting
    let cleaned = phoneNumber.replace(/[\s\-\(\)\+]/g, '')

    // Remove +27 if present
    if (cleaned.startsWith('27')) {
      cleaned = cleaned.substring(2)
    }

    // Remove leading 0 if present
    if (cleaned.startsWith('0')) {
      cleaned = cleaned.substring(1)
    }

    // Ensure it's exactly 9 digits after removing country code/leading 0
    if (cleaned.length === 9 && /^\d+$/.test(cleaned)) {
      return '+27' + cleaned
    }

    // If it's 10 digits and starts with valid SA prefix, format it
    if (cleaned.length === 10 && /^\d+$/.test(cleaned)) {
      return '+27' + cleaned.substring(1)
    }

    // Return as-is if format is unclear
    return phoneNumber
  }

  const closeTab = () => {
    dispatch(
      coreReducer.actions.closeTab({
        targetKey: 'add_patient',
        removeBreadcrumb: true
      })
    )
  }

  // Medical Aid Functions
  const handleGetMedicalAids = async (query) => {
    let tmpData = []

    setSchemeLoading(true)

    try {
      if (query) {
        tmpData = await MedPraxSchemeSearch(query)
        setSchemeData(tmpData)
      } else {
        setSchemeData([])
      }
    } catch (e) {
      message.error(handleError(e, true))
    }

    setSchemeLoading(false)
  }

  const handleGetMedicalAidPlans = async (query) => {
    let tmpData = []

    setPlansLoading(true)

    try {
      if (query) {
        tmpData = await MedPraxSchemePlanSearch(query)
        setPlansData(tmpData)
      } else {
        setPlansData([])
      }
    } catch (e) {
      message.error(handleError(e, true))
    }

    setPlansLoading(false)
  }

  const handleGetMedicalAidPlanOptions = async (query) => {
    let tmpData = []

    setPlanOptionsLoading(true)

    try {
      if (query) {
        tmpData = await MedPraxSchemePlanOptionSearch(query)
        setPlanOptionData(tmpData)
      } else {
        setPlanOptionData([])
      }
    } catch (e) {
      message.error(handleError(e, true))
    }

    setPlanOptionsLoading(false)
  }

  // eslint-disable-next-line
  const debouncedSchemeFilter = useCallback(
    debounce((query) => {
      if (query) {
        handleGetMedicalAids(query)
      } else {
        setSchemeLoading(false)
      }
    }, 1000),
    []
  )

  return {
    // State
    loading,
    patientForm,
    specialFormState,
    dependants,
    idType,
    schemeLoading,
    schemeData,
    plansLoading,
    plansData,
    planOptionsLoading,
    planOptionData,
    stayOnPage,
    savedPatient,

    // Functions
    handleSubmit,
    handleReset,
    handleDepChange,
    handleAddDep,
    handleRemoveDep,
    setIdType,
    debouncedSchemeFilter,
    handleGetMedicalAidPlans,
    handleGetMedicalAidPlanOptions,
    closeTab,
    setSpecialFormState,
    setStayOnPage
  }
}

export default useAddPatient
